
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Download, X } from "lucide-react";
import StudentDownloadFormatDialog from "./StudentDownloadFormatDialog";

interface PageSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  totalStudents: number;
  onDownload: (format: 'jpg' | 'pdf', startPage: number, endPage: number) => void;
}

const STUDENTS_PER_PAGE = 14;

const PageSelectionDialog = ({ 
  isOpen, 
  onClose, 
  totalStudents, 
  onDownload 
}: PageSelectionDialogProps) => {
  const [downloadOption, setDownloadOption] = useState("all");
  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(1);
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  const [selectedPages, setSelectedPages] = useState<{ start: number; end: number } | null>(null);

  const totalPages = Math.ceil(totalStudents / STUDENTS_PER_PAGE);

  const handleDownload = () => {
    if (downloadOption === "all") {
      setSelectedPages({ start: 1, end: totalPages });
    } else {
      const validStartPage = Math.max(1, Math.min(startPage, totalPages));
      const validEndPage = Math.max(validStartPage, Math.min(endPage, totalPages));
      setSelectedPages({ start: validStartPage, end: validEndPage });
    }
    setShowFormatDialog(true);
  };

  const handleFormatDownload = (format: 'jpg' | 'pdf') => {
    if (selectedPages) {
      onDownload(format, selectedPages.start, selectedPages.end);
    }
    onClose();
    setShowFormatDialog(false);
    setSelectedPages(null);
  };

  const handleStartPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;
    setStartPage(value);
    if (value > endPage) {
      setEndPage(value);
    }
  };

  const handleEndPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;
    setEndPage(Math.max(value, startPage));
  };

  return (
    <>
      <Dialog open={isOpen && !showFormatDialog} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Download Students List
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogTitle>
            <DialogDescription>
              Choose which pages to download
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Total Students: {totalStudents} ({totalPages} pages, {STUDENTS_PER_PAGE} students per page)
            </div>
            
            <RadioGroup value={downloadOption} onValueChange={setDownloadOption}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="all" />
                <Label htmlFor="all">Download all pages</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="range" id="range" />
                <Label htmlFor="range">Download specific page range</Label>
              </div>
            </RadioGroup>

            {downloadOption === "range" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startPage">Start Page</Label>
                  <Input
                    id="startPage"
                    type="number"
                    min={1}
                    max={totalPages}
                    value={startPage}
                    onChange={handleStartPageChange}
                  />
                  <div className="text-xs text-muted-foreground">
                    Min: 1, Max: {totalPages}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endPage">End Page</Label>
                  <Input
                    id="endPage"
                    type="number"
                    min={startPage}
                    max={totalPages}
                    value={endPage}
                    onChange={handleEndPageChange}
                  />
                  <div className="text-xs text-muted-foreground">
                    Min: {startPage}, Max: {totalPages}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleDownload} disabled={totalStudents === 0}>
                <Download className="w-4 h-4 mr-2" />
                Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <StudentDownloadFormatDialog
        isOpen={showFormatDialog}
        onClose={() => {
          setShowFormatDialog(false);
          setSelectedPages(null);
        }}
        onDownload={handleFormatDownload}
        totalStudents={totalStudents}
        startPage={selectedPages?.start}
        endPage={selectedPages?.end}
      />
    </>
  );
};

export default PageSelectionDialog;
